/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication and/or specific capability/role/group membership.
 * - If loading, shows a spinner
 * - If unauthenticated, saves current path and redirects to /signin
 * - If requiredCapability is provided (preferred gate), grants access iff the user holds
 *   that capability; otherwise redirects to a neutral destination when the user holds no
 *   capabilities (the lockout case, avoiding redirect loops) or to the role-appropriate default
 * - If requiredRoles is provided and user's role is not in the array, redirects to role-appropriate default page
 * - If requiredGroup is provided (legacy), applies the original group-based logic for backward compatibility
 *
 * The capability, role, and legacy group paths coexist so routes can migrate one at a time.
 *
 * Requirements: 5.1, 5.2, 5.5, 7.1, 7.2, 7.3, 7.4, 7.5, 13.4, 14.1
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import CrownLoader from '@/components/ui/CrownLoader';
import type { RoleType, Capability } from '@/context/AuthContext';

/**
 * Maps each role to its default landing page for redirects.
 * Exported for use in property tests (Property 4).
 */
export const ROLE_DEFAULT_ROUTES: Record<RoleType, string> = {
  admin: '/admin',
  poweruser: '/admin',
  judge: '/admin/participants',
  contestant: '/portal',
};

/**
 * Stable notice shown when an authenticated session holds no capabilities and
 * therefore cannot render any gated route. It NAVIGATES NOWHERE — that is the
 * whole point: it breaks the `/signin` ⇄ role-default redirect loop that a
 * capability-less-but-authenticated user would otherwise fall into. It offers a
 * sign-out so the user can retry with a properly-provisioned account.
 */
function AccessLockoutNotice({ onSignOut }: { onSignOut: () => void }): React.ReactElement {
  return (
    <div
      data-testid="access-lockout-notice"
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="font-display text-2xl text-foreground">Your account doesn’t have access yet</h1>
      <p className="max-w-md text-body-sm text-muted-foreground">
        You’re signed in, but this account isn’t set up with access to the portal. If you believe
        this is a mistake, please contact your pageant director. Otherwise you can sign out and try
        a different account.
      </p>
      <button
        type="button"
        onClick={onSignOut}
        className="rounded-input bg-primary px-4 py-2 text-body-sm font-medium text-primary-foreground shadow-subtle transition-colors hover:bg-primary/90"
      >
        Sign out
      </button>
    </div>
  );
}

export interface ProtectedRouteProps {
  children: React.ReactNode;
  /** New capability-based access control — the single named capability this route requires. Preferred gate. */
  requiredCapability?: Capability;
  /** Role-based access control — array of roles permitted to access this route. Retained for incremental migration. */
  requiredRoles?: RoleType[];
  /** Legacy group-based access control — kept for backward compatibility during migration. */
  requiredGroup?: 'Applicants' | 'Admins' | 'Judges';
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredCapability,
  requiredRoles,
  requiredGroup,
  redirectTo,
}: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, isLoading, isApplicant, isDirector, role, can, capabilities, signOut } =
    useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div data-testid="loading-spinner" role="status" aria-label="Loading" className="flex items-center justify-center min-h-screen">
        <CrownLoader size={64} message="Your reign awaits..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo ?? '/signin'}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // ── New capability-based access check (preferred gate; takes precedence) ─
  if (requiredCapability) {
    if (can(requiredCapability)) {
      return <>{children}</>;
    }
    // Lockout: an authenticated session that holds NO capabilities (e.g. a
    // token whose `cognito:groups` is missing the `Applicants` group) has no
    // route it can render. Redirecting such a user anywhere loops — `/signin`
    // bounces them straight back here because SignInPage auto-forwards any
    // authenticated user to their role default (`/portal`), and every other
    // gated route rejects them too. Render a STABLE, non-navigating notice
    // instead so the app fails gracefully rather than hanging the browser with
    // a redirect storm. (Requirement 5.2 lockout handling.)
    if (capabilities.size === 0) {
      return <AccessLockoutNotice onSignOut={() => void signOut()} />;
    }
    // Holds some capability but not this one → the role-appropriate default,
    // which that role can render (no loop).
    return <Navigate to={ROLE_DEFAULT_ROUTES[role]} replace />;
  }

  // ── Role-based access check (retained for incremental migration) ────────
  if (requiredRoles) {
    if (!requiredRoles.includes(role)) {
      return <Navigate to={ROLE_DEFAULT_ROUTES[role]} replace />;
    }
    return <>{children}</>;
  }

  // ── Legacy group-based access checks (backward compatibility) ───────────
  if (requiredGroup === 'Admins' && isApplicant && !isDirector) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admins can access Applicant-protected routes
  // No redirect needed for requiredGroup='Applicants' when user is Admin

  return <>{children}</>;
}
